import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GraduationCap, Award, Users, Heart, Target, CheckCircle, Calendar, MapPin, Phone, Mail, Thermometer, Sparkles, Lock, Settings, Volume2, Activity, Clock } from 'lucide-react';

const ChiSiamo = () => {
  const qualifichePrincipali = [{
    titolo: "Laurea in Terapista della Riabilitazione",
    istituto: "Università degli Studi di Messina",
    anno: "2003",
    descrizione: "Formazione universitaria completa in fisioterapia e riabilitazione"
  }, {
    titolo: "Laurea in TNPEE (Terapista della Neuro e Psicomotricità dell'Età Evolutiva)",
    istituto: "Università degli Studi di Messina",
    anno: "2006",
    descrizione: "Specializzazione nella riabilitazione neuropsicomotoria per bambini e adolescenti"
  }, {
    titolo: "Master Universitario in Riabilitazione Motoria",
    istituto: "UNIVERSITÀ \"MAGNA GRÆCIA\" di Catanzaro",
    anno: "2010",
    descrizione: "Specializzazione avanzata in tecniche di riabilitazione motoria"
  }];

  const diplomiSpecializzazioni = [{
    titolo: "Diploma Nazionale Tecnico Chinesiologo",
    istituto: "CSEN - Centro Sportivo Educativo Nazionale",
    anno: "2009",
    descrizione: "Qualifica professionale per l'analisi e correzione del movimento umano"
  }, {
    titolo: "Specializzazione in Disturbi Motori del Neonato",
    istituto: "TNPEE - Formazione Specialistica",
    anno: "2015",
    descrizione: "Trattamento plagiocefalie, torcicollo miogeno e paralisi ostetriche"
  }, {
    titolo: "Corso Avanzato Terapia Manuale Neonatale",
    istituto: "Scuola di Osteopatia Pediatrica",
    anno: "2017",
    descrizione: "Tecniche manuali specifiche per neonati e prima infanzia"
  }];

  const esperienza = [{
    periodo: "2005-2010",
    ruolo: "Fisioterapista",
    luogo: "Ospedale Policlinico - Messina",
    descrizione: "Esperienza in reparto di riabilitazione pediatrica ortopedica e neurologica"
  }, {
    periodo: "2000-Presente",
    ruolo: "Titolare Centro Papa",
    luogo: "Centro di Riabilitazione Motoria",
    descrizione: "Gestione completa del centro con focus su trattamenti personalizzati"
  }];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-primary/10 text-primary border-primary/20">
                Chi Siamo
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
                Dott. Peppino Salvatore Papa
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Fisioterapia e Chinesiologia - Terapia della Neuropsicomotricità dell'Età Evolutiva 
                con oltre 25 anni di esperienza nella riabilitazione motoria, neuropsicomotoria e 
                trattamenti specializzati per bambini, adolescenti e adulti.
              </p>
              
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">Fisioterapista</Badge>
                <Badge variant="secondary">TNPEE</Badge>
                <Badge variant="secondary">Tecnico Chinesiologo</Badge>
                <Badge variant="outline">Albo TSRM-PSTRP n. ME 67</Badge>
                <Badge variant="outline">Albo Reg. Naz. C.S.A.In</Badge>
                <Badge variant="outline">Albo ATS</Badge>
              </div>
            </div>
            
            <div className="relative">
              <div className="medical-card p-8 float-animation">
                <img src="/images/IMG_3343.jpg" alt="Dott. Peppino Salvatore Papa - Fisioterapista e TNPEE" className="w-full h-80 object-cover rounded-lg" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg soft-shadow">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">25+</div>
                  <div className="text-sm text-muted-foreground">Anni di Esperienza</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* La Mia Storia Personale */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <Badge className="bg-secondary/10 text-secondary border-secondary/20">
                La Mia Storia
              </Badge>
              <h2 className="text-4xl font-bold">Una Tradizione di Famiglia</h2>
            </div>
            
            <Card className="medical-card">
              <CardContent className="p-8">
                <div className="prose prose-lg max-w-none text-muted-foreground leading-relaxed">
                  <p className="text-lg mb-6">
                    <strong className="text-foreground">Sono Terapista e Tecnico chinesiologo</strong> e dal 2000 lavoro nel mio studio di proprietà, 
                    dove seguo personalmente ogni paziente con percorsi di riabilitazione mirati e basati sulle evidenze scientifiche.
                  </p>
                  
                  <div className="bg-primary/5 p-6 rounded-lg mb-6">
                    <p className="mb-4">
                      <strong className="text-primary">Sono letteralmente cresciuto in studio:</strong> mio padre, il <strong>Prof. Papa</strong>, 
                      terapista e chinesiologo già attivo dagli anni '70 proprietario dello studio Papa, 
                      mi ha trasmesso fin da bambino la passione per il movimento, l'attenzione al dettaglio e il rispetto per la persona.
                    </p>
                    <p>
                      Negli anni ho affiancato a questa <em>"scuola sul campo"</em> una formazione universitaria strutturata 
                      e un aggiornamento continuo tramite corsi e master specialistici.
                    </p>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-xl font-semibold text-foreground mb-4">Mi occupo in particolare di:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-start space-x-3 p-4 bg-accent/5 rounded-lg">
                        <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <Activity className="h-4 w-4 text-accent" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-foreground">Riabilitazione Ortopedica</h5>
                          <p className="text-sm">e post-chirurgica</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3 p-4 bg-primary/5 rounded-lg">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <Target className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-foreground">Rieducazione Posturale</h5>
                          <p className="text-sm">e trattamento delle scoliosi</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3 p-4 bg-secondary/5 rounded-lg">
                        <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <Award className="h-4 w-4 text-secondary" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-foreground">Ambito Sportivo</h5>
                          <p className="text-sm">prevenzione, recupero e potenziamento</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-6 rounded-lg">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Heart className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-foreground mb-2">Il Mio Obiettivo</h4>
                        <p className="text-foreground">
                          <strong>Il mio obiettivo è semplice:</strong> aiutare ogni persona a recuperare funzionalità e qualità di vita, 
                          costruendo un percorso chiaro, personalizzato e condiviso, in collaborazione con medici, 
                          specialisti e società sportive del territorio.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Foto Storica - Anno 2000 */}
            <div className="mt-12">
              <Card className="medical-card overflow-hidden">
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    {/* Immagine */}
                    <div className="relative h-96 lg:h-auto">
                      <img 
                        src="/images/IMG_3532.png" 
                        alt="Dott. Peppino Papa con il Prof. Papa nello studio - Anno 2000" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-black/70 text-white border-white/30">
                          Anno 2000
                        </Badge>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                        <p className="text-white text-sm font-medium">
                          Con mio padre, il Prof. Papa, nel suo studio
                        </p>
                      </div>
                    </div>
                    
                    {/* Testo */}
                    <div className="p-8 bg-gradient-to-br from-primary/5 to-secondary/5 flex flex-col justify-center">
                      <div className="space-y-6">
                        <div>
                          <Badge className="bg-secondary/10 text-secondary border-secondary/20 mb-4">
                            Una Tradizione che Continua
                          </Badge>
                          <h3 className="text-2xl font-bold mb-4">Dove Tutto È Iniziato</h3>
                        </div>
                        
                        <div className="space-y-4 text-muted-foreground">
                          <p className="leading-relaxed">
                            Questa foto, scattata nell'<strong className="text-foreground">anno 2000</strong>, 
                            mi ritrae insieme a mio padre, il <strong className="text-foreground">Prof. Papa</strong>, 
                            nel suo storico studio. Quello stesso studio dove ho mosso i primi passi 
                            in questo mondo, osservando, imparando e crescendo.
                          </p>
                          
                          <p className="leading-relaxed">
                            Da bambino passavo ore in quello studio, affascinato dal lavoro di mio padre 
                            e dalla dedizione con cui si prendeva cura di ogni paziente. 
                            <strong className="text-foreground"> Quello studio, oggi completamente rimodernato</strong>, 
                            è diventato il mio centro, ma conserva ancora l'anima e i valori 
                            che mio padre mi ha trasmesso.
                          </p>
                          
                          <div className="bg-white/50 p-4 rounded-lg border-l-4 border-primary">
                            <p className="text-sm italic text-foreground">
                              "La passione per il movimento, l'attenzione al dettaglio e il rispetto 
                              per la persona: questi sono i valori che ho ereditato e che porto 
                              avanti ogni giorno nel mio lavoro."
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4 pt-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-primary rounded-full"></div>
                            <span className="text-sm font-medium">Anni '70 - Prof. Papa apre lo studio</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-secondary rounded-full"></div>
                            <span className="text-sm font-medium">2000 - Continua la tradizione</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifiche Principali */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Formazione
            </Badge>
            <h2 className="text-4xl font-bold">Titoli Universitari</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {qualifichePrincipali.map((qualifica, index) => (
              <Card key={index} className="medical-card hover:scale-105 smooth-transition">
                <CardHeader>
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <GraduationCap className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-center text-lg">{qualifica.titolo}</CardTitle>
                  <CardDescription className="text-center">{qualifica.istituto}</CardDescription>
                </CardHeader>
                <CardContent className="text-center space-y-2">
                  <Badge variant="outline">{qualifica.anno}</Badge>
                  <p className="text-sm text-muted-foreground">{qualifica.descrizione}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Il Mio Percorso Professionale */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-secondary/10 text-secondary border-secondary/20">
              Esperienza
            </Badge>
            <h2 className="text-4xl font-bold">Il Mio Percorso Professionale</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {esperienza.map((exp, index) => (
              <Card key={index} className="medical-card">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-8 w-8 text-secondary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold">{exp.ruolo}</h3>
                          <p className="text-lg text-muted-foreground">{exp.luogo}</p>
                        </div>
                        <Badge variant="outline" className="w-fit">{exp.periodo}</Badge>
                      </div>
                      <p className="text-muted-foreground">{exp.descrizione}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Convenzione Universitaria */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <Badge className="bg-accent/10 text-accent border-accent/20">
                Formazione e Tirocini
              </Badge>
              <h2 className="text-4xl font-bold">Convenzione Universitaria</h2>
            </div>

            <Card className="medical-card">
              <CardContent className="p-8">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="h-8 w-8 text-accent" />
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Centro Convenzionato per Tirocini Curriculari</h3>
                      <p className="text-lg text-primary font-semibold">Università degli Studi di Messina</p>
                    </div>
                    
                    <div className="bg-accent/5 p-6 rounded-lg">
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Il nostro centro è <strong className="text-foreground">convenzionato per tirocini curriculari</strong> con 
                        l'Università degli Studi di Messina, offrendo agli studenti l'opportunità di formarsi 
                        in un ambiente professionale specializzato.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <div className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-foreground">Area Riabilitativa</h4>
                            <p className="text-sm text-muted-foreground">
                              Tirocini in fisioterapia e riabilitazione motoria
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-semibold text-foreground">Area Sportiva</h4>
                            <p className="text-sm text-muted-foreground">
                              Formazione in chinesiologia e preparazione atletica
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-4">
                      <Badge variant="secondary">Tirocini Curriculari</Badge>
                      <Badge variant="secondary">Formazione Pratica</Badge>
                      <Badge variant="secondary">Supervisione Qualificata</Badge>
                      <Badge variant="secondary">Esperienza Professionale</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* La Nostra Struttura */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <Badge className="bg-primary/10 text-primary border-primary/20">
                La Nostra Struttura
              </Badge>
              <h2 className="text-4xl font-bold">Ambiente Accogliente e Confortevole</h2>
              <p className="text-xl text-muted-foreground">
                Un centro moderno e curato per il tuo benessere in ogni stagione
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="medical-card">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Thermometer className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Climatizzazione Ottimale</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Ambiente <strong>climatizzato</strong> per garantire il massimo comfort durante 
                    l'attività fisica in <strong>ogni periodo dell'anno</strong>, sia estivo che invernale. 
                    Temperature controllate per un'esperienza ottimale.
                  </p>
                </CardContent>
              </Card>

              <Card className="medical-card">
                <CardHeader>
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mb-4">
                    <Sparkles className="h-6 w-6 text-secondary" />
                  </div>
                  <CardTitle>Ambiente Curato</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Spazi <strong>accoglienti e confortevoli</strong> con particolare attenzione 
                    all'igiene e alla pulizia. Ogni dettaglio è pensato per creare un'atmosfera 
                    rilassante e professionale.
                  </p>
                </CardContent>
              </Card>

              <Card className="medical-card">
                <CardHeader>
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mb-4">
                    <Settings className="h-6 w-6 text-accent" />
                  </div>
                  <CardTitle>Attrezzature Moderne</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Dotato di <strong>attrezzature professionali</strong> certificate e tecnologie 
                    avanzate per trattamenti efficaci. Strumentazione BTL di ultima generazione 
                    per terapie strumentali.
                  </p>
                </CardContent>
              </Card>

              <Card className="medical-card">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Volume2 className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>Ambiente Tranquillo</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Spazi <strong>silenziosi e riservati</strong> che favoriscono il rilassamento 
                    e la concentrazione durante i trattamenti. Privacy garantita per ogni paziente.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12">
              <Card className="medical-card bg-gradient-to-r from-primary/5 to-secondary/5">
                <CardContent className="p-8">
                  <div className="text-center space-y-4">
                    <h3 className="text-2xl font-bold">Perché Scegliere il Nostro Centro</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                      <div className="space-y-2">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                          <Heart className="h-6 w-6 text-primary" />
                        </div>
                        <h4 className="font-semibold">Comfort Garantito</h4>
                        <p className="text-sm text-muted-foreground">
                          Temperatura ideale tutto l'anno per l'attività fisica
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mx-auto">
                          <Lock className="h-6 w-6 text-secondary" />
                        </div>
                        <h4 className="font-semibold">Privacy e Riservatezza</h4>
                        <p className="text-sm text-muted-foreground">
                          Spazi dedicati per trattamenti personalizzati
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto">
                          <Sparkles className="h-6 w-6 text-accent" />
                        </div>
                        <h4 className="font-semibold">Igiene Impeccabile</h4>
                        <p className="text-sm text-muted-foreground">
                          Pulizia e sanificazione costante degli ambienti
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChiSiamo;